import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Loader2, CheckCircle2, Receipt, CreditCard } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AgencyInvoicePay() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paid, setPaid] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    // If returning from Stripe, confirm payment first.
    if (sessionId) {
      try {
        const st = await axios.get(`${API}/public/agency-tx/invoice/${token}/status`, { params: { session_id: sessionId } });
        if (st.data.payment_status === 'paid' || st.data.invoice_status === 'paid') { setPaid(true); setLoading(false); return; }
      } catch { /* fall through */ }
    }
    try { const r = await axios.get(`${API}/public/agency-tx/invoice/${token}`); setInv(r.data); }
    catch (e) {
      const d = e?.response?.data?.detail;
      if (d && d.status === 'paid') { setPaid(true); } else { setError((d && d.message) || 'This invoice could not be found.'); }
    } finally { setLoading(false); }
  }, [token, sessionId]);
  useEffect(() => { load(); }, [load]);

  const pay = async () => {
    setBusy(true); setError('');
    try {
      const r = await axios.post(`${API}/public/agency-tx/invoice/${token}/checkout`, { guest_email: email || undefined, origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) { setError(e?.response?.data?.detail || 'Could not start checkout'); setBusy(false); }
  };

  if (loading) return <Center><Loader2 className="w-6 h-6 animate-spin text-teal-600" /></Center>;

  if (paid) return (
    <Center><div className="bg-white rounded-xl shadow-sm p-8 text-center max-w-md" data-testid="invoice-paid-state">
      <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
      <h2 className="text-xl font-bold mt-3">Payment received</h2>
      <p className="text-slate-500 mt-1">Thank you! A receipt has been emailed to you.</p>
    </div></Center>
  );
  if (error && !inv) return <Center><div className="text-slate-500">{error}</div></Center>;

  const due = inv.amount_due_cents;
  return (
    <div className="min-h-screen bg-slate-100 py-10 px-4" data-testid="public-invoice-pay">
      <div className="max-w-xl mx-auto bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="bg-teal-700 text-white px-6 py-5">
          <div className="text-sm opacity-80">{inv.agency_name}</div>
          <div className="flex items-center gap-2 mt-1"><Receipt className="w-5 h-5" /><h1 className="text-xl font-bold">Invoice {inv.invoice_number}</h1></div>
        </div>
        <div className="p-6">
          <table className="w-full text-sm mb-4">
            <tbody>{(inv.items || []).map((it) => (
              <tr key={it.id} className="border-b"><td className="py-2">{it.description} <span className="text-slate-400">×{it.quantity}</span></td><td className="py-2 text-right">{money(it.amount_cents)}</td></tr>
            ))}</tbody>
          </table>
          <div className="space-y-1 text-sm">
            <Row label="Subtotal" val={money(inv.subtotal_cents)} />
            {inv.discount_cents > 0 && <Row label="Discount" val={`−${money(inv.discount_cents)}`} />}
            {inv.tax_cents > 0 && <Row label={`Tax (${inv.tax_pct}%)`} val={money(inv.tax_cents)} />}
            {inv.amount_paid_cents > 0 && <Row label="Already paid" val={`−${money(inv.amount_paid_cents)}`} />}
            <div className="flex justify-between font-bold text-lg pt-2 border-t"><span>Amount due</span><span>{money(due)}</span></div>
          </div>
          <div className="mt-5 space-y-3">
            <div><label className="text-sm font-medium">Email for receipt</label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="receipt-email-input" /></div>
            {error && <div className="text-sm text-rose-600">{error}</div>}
            <Button className="w-full bg-teal-600 hover:bg-teal-700" disabled={busy || due <= 0} onClick={pay} data-testid="pay-invoice-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CreditCard className="w-4 h-4 mr-2" />Pay {money(due)}</>}
            </Button>
          </div>
        </div>
        <div className="px-6 py-3 bg-slate-50 text-center text-xs text-slate-400">Secure payments powered by iEvntür · Stripe</div>
      </div>
    </div>
  );
}

const Center = ({ children }) => <div className="min-h-screen flex items-center justify-center bg-slate-100 px-4">{children}</div>;
const Row = ({ label, val }) => <div className="flex justify-between"><span className="text-slate-500">{label}</span><span>{val}</span></div>;
